<template>
  <b-modal
    id="applicant-modal"
    :title="modalTitle"
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    centered
    dialog-class="m-0 p-0 w-100 h-100"
    content-class="h-100"
    body-class="h-100 p-0"
    @show="handleShow"
    @hidden="handleHide"
  >
    <!-- Navigation buttons -->
    <div
      class="d-flex justify-content-between align-items-center navigation-buttons"
      v-if="isModalVisible"
    >
      <b-button
        size="sm"
        variant="secondary"
        @click="goToPrevious"
        :disabled="disablePrevButton"
        >Previous</b-button
      >
      <div class="small mx-2">
        {{ currentApplicant + 1 }} out of {{ applicantData?.length }}
      </div>
      <b-button
        size="sm"
        variant="secondary"
        @click="goToNext"
        :disabled="disableNextButton"
        >Next</b-button
      >
    </div>

    <div v-if="isModalVisible" class="d-flex flex-column h-100">
      <div class="d-flex flex-column h-100 px-2 py-1" style="overflow: hidden">
        <!-- Top Half -->
        <div class="d-flex flex-wrap mb-1 top-half">
          <!-- Personal -->
          <div class="m-1 d-flex flex-column personal">
            <h5 class="mb-1">Personal</h5>
            <hr
              class="mb-1 mt-0"
              style="margin-left: 0.08rem; margin-right: 0.1rem"
            />
            <p class="mb-0">
              <strong>Name:</strong>
              {{
                `${currentApplicantData?.first_name} ${currentApplicantData?.last_name}`
              }}
            </p>
            <p class="mb-0">
              <strong>Email:</strong>
              {{ currentApplicantData?.email }}
            </p>
            <p class="mb-0">
              <strong>Gender:</strong>
              {{ currentApplicantData?.gender }}
            </p>
            <p class="mb-0">
              <strong>Education:</strong>
              {{
                `${currentApplicantEducationDetails?.degree} in ${currentApplicantEducationDetails?.stream}`
              }}
            </p>
            <p class="mb-0">
              <strong>College:</strong>
              {{ currentApplicantData?.college_name }}
            </p>
            <p class="mb-0">
              <strong>Drive:</strong>
              {{ recruitmentDrive ?? "-" }}
            </p>
          </div>

          <!-- Education Details -->
          <div class="m-1 d-flex flex-column education-card">
            <h5 class="mb-1">Educational</h5>
            <hr
              class="mb-1 mt-0"
              style="margin-left: 0.1rem; margin-right: 0.1rem"
            />
            <div class="d-flex justify-content-between">
              <p class="mb-0" style="font-size: 0.85rem">
                <strong>Aggregate:</strong>
                {{ currentApplicantEducationDetails?.aggregate_percentage }}
              </p>
              <p class="mb-0" style="font-size: 0.85rem">
                <strong>HSC:</strong>
                {{ currentApplicantEducationDetails?.hsc_percentage }}
              </p>
            </div>

            <div class="d-flex justify-content-between">
              <p class="mb-0" style="font-size: 0.85rem">
                <strong>Graduation:</strong>
                {{ currentApplicantEducationDetails?.grad_percentage }}
              </p>
              <p class="mb-0" style="font-size: 0.85rem">
                <strong>SSC:</strong>
                {{ currentApplicantEducationDetails?.ssc_percentage }}
              </p>
            </div>

            <div
              v-for="row in 4"
              :key="row"
              class="d-flex justify-content-between"
            >
              <p class="mb-0" style="font-size: 0.85rem">
                <strong>Sem {{ (row - 1) * 2 + 1 }}:</strong>
                {{
                  currentApplicantEducationDetails?.[
                    `semester${(row - 1) * 2 + 1}`
                  ]
                }}
              </p>
              <p class="mb-0" style="font-size: 0.85rem">
                <strong>Sem {{ (row - 1) * 2 + 2 }}:</strong>
                {{
                  currentApplicantEducationDetails?.[
                    `semester${(row - 1) * 2 + 2}`
                  ]
                }}
              </p>
            </div>
          </div>

          <div class="interviews">
            <!-- Aptitude Round -->
            <div
              v-for="(round, index) in writtenTestRounds"
              :key="`aptitude-round-${index}`"
              class="m-1 aptitude"
            >
              <div
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <h5 class="mb-0">Aptitude Round {{ index + 1 }}</h5>
                <span
                  v-if="round.grade"
                  class="gradebadge"
                  :style="{
                    backgroundColor: getGradeBadge(round.grade).color,
                    cursor: 'pointer',
                  }"
                  :title="round.grade"
                  data-toggle="tooltip"
                >
                  {{ getGradeBadge(round.grade).initial }}
                </span>
              </div>

              <hr class="mb-1 mt-0" />
              <p v-if="round.score && round.outOf" class="mb-0">
                <strong>Score:</strong>
                {{ `${round.score} out of ${round.outOf}` }}
              </p>
              <div class="aptitude-content">
                <p class="mb-1" v-if="round.comments">
                  <strong>Commnets:</strong>
                  {{ round.comments }}
                </p>
              </div>
            </div>

            <!-- Past Interviews -->
            <div
              v-for="(round, roundIdx) in interviewerRemarks"
              :key="roundIdx"
              class="mx-1 mt-1 mb-0 interview-remarks-div"
              :class="{ 'mr-1': roundIdx != interviewerRemarks?.length - 1 }"
            >
              <div>
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="mb-1">{{ round.roundTitle }}</h5>
                  <span
                    v-if="round.grade"
                    class="gradebadge"
                    :style="{
                      backgroundColor: getGradeBadge(round.grade).color,
                      cursor: 'pointer',
                    }"
                    :title="round.grade"
                    data-toggle="tooltip"
                  >
                    {{ getGradeBadge(round.grade).initial }}
                  </span>
                </div>
                <hr class="mb-1 mt-0" />
                <p class="mb-0">
                  <strong>Interviewer:</strong> {{ round.interviewerName }}
                </p>
              </div>
              <div class="interview-content">
                <ul class="list-unstyled mb-0">
                  <li
                    v-for="(value, key) in round.remarks"
                    :key="key"
                    style="font-size: 0.85rem"
                  >
                    <strong>{{ key }}:</strong> {{ value }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom Half + PiP -->
        <div
          class="d-flex position-relative align-items-start"
          style="overflow: hidden; height: 52%"
        >
          <div class="m-1 d-flex flex-column current-round">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-1">
                {{
                  currentApplicantCurrentRoundData?.eventSlot
                    ?.belongToCampusEvents?.round_title ?? "Current Round"
                }}
              </h5>
              <span
                v-if="currentApplicantCurrentRoundData?.grade"
                class="gradebadge"
                :style="{
                  backgroundColor: getGradeBadge(
                    currentApplicantCurrentRoundData?.grade
                  ).color,
                  cursor: 'pointer',
                }"
                :title="currentApplicantCurrentRoundData?.grade"
                data-toggle="tooltip"
              >
                {{
                  getGradeBadge(currentApplicantCurrentRoundData?.grade).initial
                }}
              </span>
            </div>
            <hr
              class="mb-1 mt-0"
              style="margin-left: 0.1rem; margin-right: 0.1rem"
            />
            <div class="form-container">
              <div
                v-for="(section, idx) in getCurrentForm()?.sections"
                :key="idx"
              >
                <b-card
                  v-if="section.title || section.description"
                  class="no-border"
                >
                  <div class="row no-gutters">
                    <div
                      v-for="question in groupDependentsNextToParents(
                        section.body
                      )"
                      :key="question.key"
                      v-show="shouldRenderQuestion(question)"
                      class="col-12 col-md-6 mb-3 mr-2"
                    >
                      <div style="max-width: 300px; width: 100%">
                        <component
                          :ref="question.key"
                          :is="question.type + '-renderer'"
                          v-bind="question"
                          v-model="testInput[question.label]"
                          :disabled="disabled"
                          @update:modelValue="
                            (val) => (testInput[question.label] = val)
                          "
                        ></component>
                      </div>
                    </div>
                  </div>
                </b-card>
              </div>
            </div>
          </div>

          <!-- PiP as a flex item -->
          <div
            class="m-1 meet-pip d-flex flex-column align-items-center justify-content-center"
          >
            <div
              class="icon-circle bg-secondary d-flex align-items-center justify-content-center mb-1"
            >
              <b-icon icon="camera-video" variant="light" font-scale="2" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import api from "../_api/index";
import PERMISSIONS from "../../Common/permissions";
import { hasAccess } from "../../User/_utils/actions";
import LineInputRenderer from "../../FormTemplate/_components/renderers/LineInputRenderer.vue";
import ParagraphInputRenderer from "../../FormTemplate/_components/renderers/ParagraphInputRenderer.vue";
import RatingInputRenderer from "../../FormTemplate/_components/renderers/RatingInputRenderer.vue";
import DropDownInputRenderer from "../../FormTemplate/_components/renderers/DropDownInputRenderer.vue";
import CheckboxInputRenderer from "../../FormTemplate/_components/renderers/CheckboxInputRenderer.vue";
import RadioInputRenderer from "../../FormTemplate/_components/renderers/RadioInputRenderer.vue";
import FileInputRenderer from "../../FormTemplate/_components/renderers/FileInputRenderer.vue";
import DateInputRenderer from "../../FormTemplate/_components/renderers/DateInputRenderer.vue";

export default {
  name: "ApplicantModal",
  components: {
    "line-renderer": LineInputRenderer,
    "paragraph-renderer": ParagraphInputRenderer,
    "rating-renderer": RatingInputRenderer,
    "dropdown-renderer": DropDownInputRenderer,
    "checkbox-renderer": CheckboxInputRenderer,
    "radio-renderer": RadioInputRenderer,
    "file-renderer": FileInputRenderer,
    "date-renderer": DateInputRenderer,
  },
  props: {
    applicantId: {
      type: Number,
      default: () => 0,
    },
    myFilters: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      applicantData: null,
      currentApplicant: null,
      isModalVisible: false,
      recruitmentDrive: null,
      testInput: {},
    };
  },
  watch: {
    testInput: {
      handler(newVal) {
        if (this.currentApplicantCurrentRoundData?.round_result != "Pending")
          return;
        this.debouncedSave(newVal);
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters({
      formTemplates: "$_form_template/getForms",
      loggedInUser: "user/loggedInUser",
    }),
    PERMISSIONS() {
      return PERMISSIONS;
    },
    BASE_PERMISSION() {
      return this.PERMISSIONS.APPLICANT;
    },
    disabled() {
      // disable fields when
      // - round result is not pending
      if (this.currentApplicantCurrentRoundData?.round_result != "Pending")
        return true;

      // enable fields when
      // - the user has permission to update the round
      if (this.hasAccess({ permissions: [this.BASE_PERMISSION.ROUND.UPDATE] }))
        return false;
      // disable fields when
      // - logged in user is not assigned as interviewer
      else
        return !this.currentRoundInterviewers?.some(
          (interviewer) =>
            interviewer.interviewer_id == this.loggedInUser?.user_id
        );
    },
    currentApplicantData() {
      return this.applicantData?.[this.currentApplicant];
    },
    currentApplicantEducationDetails() {
      return this.currentApplicantData?.education_details;
    },
    currentApplicantCurrentRoundData() {
      return this.currentApplicantData?.currentRoundData;
    },
    currentRoundInterviewers() {
      return this.currentApplicantCurrentRoundData?.interviewers;
    },
    modalTitle() {
      const applicant = this.currentApplicantData;
      if (!applicant) return "Applicant Interview Details";
      return `Applicant Interview Details: ${applicant.first_name} ${applicant.last_name}`;
    },
    disableNextButton() {
      return this.currentApplicant == this.applicantData?.length - 1;
    },
    disablePrevButton() {
      return this.currentApplicant == 0;
    },
    writtenTestRounds() {
      const applicant = this.applicantData?.[this.currentApplicant];
      const events = applicant?.oldDependentEvent || [];

      return [...events]
        .sort()
        .filter(
          (round) =>
            round.round_name &&
            round.round_name == "Written Test" &&
            round.id != this.currentApplicantCurrentRoundData.id
        );
    },
    interviewRounds() {
      const applicant = this.applicantData?.[this.currentApplicant];
      const events = applicant?.oldDependentEvent || [];

      return [...events]
        .sort()
        .filter(
          (round) =>
            round.round_name &&
            round.round_name != "Written Test" &&
            round.id != this.currentApplicantCurrentRoundData.id
        );
    },
    interviewerRemarks() {
      if (!this.interviewRounds || !Array.isArray(this.interviewRounds))
        return [];

      return this.interviewRounds.flatMap((round) => {
        return (round.interviewers || []).map((interviewer) => {
          let remarks = {};
          try {
            remarks = JSON.parse(interviewer.interviewer_remarks || "{}");
          } catch (e) {
            console.error("Invalid JSON in interviewer_remarks", e);
          }

          return {
            roundTitle:
              round.eventSlot?.belongToCampusEvents?.round_title || "",
            interviewerName:
              `${interviewer.first_name} ${interviewer.last_name}`.trim() ||
              "Unnamed Interviewer",
            remarks,
            grade: round.grade,
          };
        });
      });
    },
  },
  methods: {
    goToPrevious() {
      this.currentApplicant--;
      this.testInput = {};
      if (
        this.currentRoundInterviewers?.[0].interviewer_remarks &&
        this.currentRoundInterviewers?.[0].interviewer_remarks != "null"
      ) {
        const testInputRaw = JSON.parse(
          this.currentRoundInterviewers?.[0].interviewer_remarks
        );
        for (const rawKey in testInputRaw) {
          const value = testInputRaw[rawKey];
          let matchedLabel = null;

          // 1. Check for exact match
          if (
            this.getCurrentForm()?.sections?.some((section) =>
              section.body.some((q) => q.key === rawKey)
            )
          ) {
            matchedLabel = this.getCurrentForm()
              .sections.flatMap((section) => section.body)
              .find((q) => q.key === rawKey)?.label;
          }

          // 2. Check for camelCase label match
          if (!matchedLabel) {
            matchedLabel = this.camelToLabel(rawKey);
          }

          // 3. Fallback to rawKey as label
          if (!matchedLabel) {
            matchedLabel = rawKey;
          }

          this.$set(this.testInput, matchedLabel, value);
        }
      }
    },
    goToNext() {
      this.currentApplicant++;
      this.testInput = {};
      if (
        this.currentRoundInterviewers?.[0].interviewer_remarks &&
        this.currentRoundInterviewers?.[0].interviewer_remarks != "null"
      ) {
        const testInputRaw = JSON.parse(
          this.currentRoundInterviewers?.[0].interviewer_remarks
        );
        for (const rawKey in testInputRaw) {
          const value = testInputRaw[rawKey];
          let matchedLabel = null;

          // 1. Check for exact match
          if (
            this.getCurrentForm()?.sections?.some((section) =>
              section.body.some((q) => q.key === rawKey)
            )
          ) {
            matchedLabel = this.getCurrentForm()
              .sections.flatMap((section) => section.body)
              .find((q) => q.key === rawKey)?.label;
          }

          // 2. Check for camelCase label match
          if (!matchedLabel) {
            matchedLabel = this.camelToLabel(rawKey);
          }

          // 3. Fallback to rawKey as label
          if (!matchedLabel) {
            matchedLabel = rawKey;
          }

          this.$set(this.testInput, matchedLabel, value);
        }
      }
    },
    camelToLabel(camelStr) {
      return camelStr
        .replace(/([a-z])([A-Z])/g, "$1 $2") // insert space *between* lower & upper
        .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter
    },
    hasAccess({ permissions, checkAll }) {
      return hasAccess({ permissions, checkAll });
    },
    saveTestInput(data) {
      const payload = {
        formMasterId: this.currentRoundInterviewers?.[0].formMasterId,
        formVersionId: this.currentRoundInterviewers?.[0].formVersionId,
        id: this.currentRoundInterviewers?.[0].id,
        interviewer_remarks: JSON.stringify(data),
      };
      api
        .interviewRemarkApi(payload)
        .then(() => {
          this.$store.dispatch(
            "openSnackbar",
            { type: "success", message: "Data updated successfully!" },
            { root: true }
          );
          this.fetchAndInitializeApplicants(false);
        })
        .catch(() => {});
    },
    loadRemarksIntoTestInput() {
      const remarks = this.currentRoundInterviewers?.[0]?.interviewer_remarks;

      if (remarks && remarks !== "null") {
        try {
          const testInputRaw = JSON.parse(remarks);
          const formQuestions =
            this.getCurrentForm()?.sections?.flatMap((s) => s.body) || [];

          for (const rawKey in testInputRaw) {
            const value = testInputRaw[rawKey];
            let matchedLabel = null;

            // 1. Check for exact match in question.key
            const questionByKey = formQuestions.find((q) => q.key === rawKey);
            if (questionByKey) {
              matchedLabel = questionByKey.label;
            }

            // 2. If not found, try camelCase label match
            if (!matchedLabel) {
              matchedLabel = this.camelToLabel(rawKey);
            }

            // 3. Fallback to rawKey
            if (!matchedLabel) {
              matchedLabel = rawKey;
            }

            this.$set(this.testInput, matchedLabel, value);
          }
        } catch (e) {
          console.error("Failed to parse interviewer_remarks", e);
        }
      } else {
        this.testInput = {};
      }
    },
    async fetchAndInitializeApplicants(loadCurrentApplicant) {
      try {
        const { data } = await axios.post(
          `applicants/my_schedule/campus_applicant_list/${this.myFilters.eventId}`,
          {},
          { params: this.myFilters }
        );

        // Map eventId -> event object
        const mappedEvent = data.events.reduce((obj, e) => {
          e.interviewers = {};
          obj[e.id] = e;
          return obj;
        }, {});

        const formIds = {};

        const handleForms = (inter, eventId) => {
          if (!formIds[eventId]) formIds[eventId] = [];
          const exist = formIds[eventId].find(
            (form) =>
              form.formMasterId == inter.formMasterId &&
              form.formVersionId == inter.formVersionId
          );
          if (!exist) {
            formIds[eventId].push({
              formMasterId: inter.formMasterId,
              formVersionId: inter.formVersionId,
              interviewers: {
                [inter.user_id]: inter.user_id,
              },
            });
          } else {
            exist["interviewers"][inter.user_id] = inter.user_id;
          }
        };

        // Prepare applicants
        data.applicants.forEach((a) => {
          a.hasRounds = a.oldDependentEvent.reduce((obj, r) => {
            if (r.eventSlot.roundId) {
              obj[r.eventSlot.roundId] = r;
              obj[r.eventSlot.roundId]["mappedInterviewers"] = {};
              if (
                mappedEvent[r.eventSlot.roundId] &&
                mappedEvent[r.eventSlot.roundId]["round_name"] === "Interview"
              ) {
                for (const it of r.interviewers) {
                  this.handleFormVersion(it);
                  obj[r.eventSlot.roundId]["mappedInterviewers"][it.user_id] =
                    it;
                  handleForms(it, r.eventSlot.roundId);
                  mappedEvent[r.eventSlot.roundId]["interviewers"][it.user_id] =
                    it;
                }
              }
            }
            return obj;
          }, {});
        });

        // Set top-level data
        this.recruitmentDrive =
          data.events?.[0]?.campusDetail?.recruitment_session_name;
        this.applicantData = data.applicants;
        if (loadCurrentApplicant)
          this.currentApplicant = this.applicantData.findIndex(
            (applicant) => applicant.id == this.applicantId
          );

        // Load current applicant's form answers
        this.loadRemarksIntoTestInput();
      } catch (err) {
        console.error("Failed to fetch applicant data", err);
      }
    },
    debounce(func, wait) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },
    handleFormVersion(it) {
      if (it.formMasterId && !it.formVersionId) {
        const formMasterIdData = (this.formTemplates || []).find(
          (f) => f.id === it.formMasterId
        );
        const formData = formMasterIdData.formVersion[0];
        if (formData) {
          it.formVersionId = formData.id;
        } else {
          it.formMasterId = null;
        }
      } else if (it.formVersionId && !it.formMasterId) {
        const formMasterIdData = (this.formTemplates || []).find((f) => {
          const childVersion = f.formVersion.find(
            (cv) => cv.id === it.formVersionId
          );
          return !!childVersion;
        });
        if (formMasterIdData) {
          it.formMasterId = formMasterIdData.id;
        } else {
          it.formMasterId = null;
          it.formVersionId = null;
        }
      } else if (!it.formMasterId && !it.formVersionId) {
        it.formMasterId = null;
        it.formVersionId = null;
      }
    },
    handleShow() {
      this.isModalVisible = true;
      this.fetchAndInitializeApplicants(true);
    },
    handleHide() {
      this.isModalVisible = false;
      this.currentApplicant = null;
      this.testInput = {};
      this.$emit("modal-closed");
    },
    getCurrentForm() {
      const formMasterId = this.currentRoundInterviewers?.[0]?.formMasterId;
      const formVersionId = this.currentRoundInterviewers?.[0]?.formVersionId;

      const formMaster = this.formTemplates.find(
        (form) => form.id == formMasterId
      );

      const formVersion = formVersionId
        ? formMaster?.formVersion?.find(
            (version) => version.id == formVersionId
          )
        : formMaster?.formVersion?.[0];

      return formVersion?.form_json;
    },
    groupDependentsNextToParents(array) {
      const result = [];

      for (const question of array) {
        // take only those question which are not dependent (bcoz dependent question will eventually fall when their parent will come)
        if (!question.showConditionally) {
          // push the non-dependent questions first and then check for its dependent questions
          result.push(question);
          // if the non-dependent question is radio then only check for its group
          if (question.type == "radio")
            for (const q of array) {
              if (
                q.showConditionally &&
                q.conditionalQuestion.key === question.key
              ) {
                result.push(q);
              }
            }
        }
      }

      return result;
    },
    shouldRenderQuestion(question) {
      if (!question.showConditionally) return true;

      const condition = question.conditionalQuestion;
      if (!condition?.key || condition.value == null) return false;

      const selectedValue = this.testInput[condition.questionLabel];

      return selectedValue === condition.value;
    },
    getGradeBadge(grade) {
      const gradeMap = {
        Excellent: { initial: "E", color: "#28a745" }, // Green
        "Very Good": { initial: "VG", color: "#17a2b8" }, // Teal
        Good: { initial: "G", color: "#007bff" }, // Blue
        Moderate: { initial: "M", color: "#ffc107" }, // Yellow
        Lacking: { initial: "L", color: "#fd7e14" }, // Orange
        Weak: { initial: "W", color: "#dc3545" }, // Red
        Poor: { initial: "P", color: "#6f42c1" }, // Purple
      };
      return gradeMap[grade] || { initial: "?", color: "#6c757d" }; // Default grey
    },
  },
  created() {
    this.debouncedSave = this.debounce(this.saveTestInput, 500);
  },
};
</script>

<style scoped>
::v-deep .modal-dialog {
  max-width: 100% !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
}

::v-deep .modal-content {
  height: 100% !important;
  border-radius: 0 !important;
}

::v-deep .modal-body {
  height: 100% !important;
  overflow: hidden !important;
  padding: 0 !important;
}

::v-deep .card-body {
  padding: 0.3rem !important;
}

::v-deep .modal-header {
  padding: 0.5rem !important;
}

.col-md-6 {
  max-width: 23.5% !important;
}

.col-12 {
  max-width: 23.5% !important;
}

::v-deep .row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.education-card {
  flex: 0 0 15% !important;
  min-width: 15% !important;
  height: 98% !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem !important;
  overflow-y: auto !important;
  padding: 0.3rem !important;
}

.education-card::-webkit-scrollbar {
  width: 6px !important;
}

.education-card::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3) !important;
  border-radius: 3px !important;
}

.education-card::-webkit-scrollbar-track {
  background: transparent !important;
}

strong,
h5 {
  font-weight: 600 !important;
}

.no-border {
  border: none !important;
}

.navigation-buttons {
  padding: 0.5rem 0.75rem 0rem 0.75rem !important;
}

.top-half {
  flex: 0 0 40% !important;
  max-height: 40% !important;
  height: 40% !important;
  font-size: 0.85rem !important;
}

.personal {
  flex: 0 0 20% !important;
  min-width: 20% !important;
  height: 98% !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem !important;
  overflow-y: auto !important;
  padding: 0.3rem !important;
  box-sizing: border-box !important;
  overflow-y: auto !important;
}

.aptitude {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
  min-width: 25% !important;
  max-width: 100% !important;
  height: 98% !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem !important;
  padding: 0.3rem !important;
}

.aptitude-content {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
  overflow-y: auto !important;
}

.aptitude hr {
  height: 1px !important;
  width: 100% !important;
  border: none !important;
  background-color: #dee2e6 !important;
  flex-shrink: 0 !important;
}

.interview-remarks-div {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
  min-width: 25% !important;
  max-width: 100% !important;
  height: 98% !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem !important;
  padding: 0.3rem !important;
  margin-right: 0.25rem !important;
}

.gradebadge {
  color: #fff !important;
  border-radius: 50% !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 1.5rem !important;
  height: 1.5rem !important;
  font-size: 0.85rem !important;
  margin-left: 0.5rem !important;
}

.interviews {
  display: flex !important;
  flex: 1 1 0 !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  min-width: 25% !important;
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  height: 100% !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

.interviews::-webkit-scrollbar {
  width: 0px !important;
  height: 0px !important;
  background: transparent !important;
}

.interview-content {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
}

.icon-circle {
  width: 30% !important;
  height: 35% !important;
  border-color: transparent !important;
  border-radius: 50% !important;
}

.current-round {
  flex: 1 1 0 !important;
  height: 98% !important;
  overflow: hidden !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem !important;
  padding: 0.3rem !important;
}

.form-container {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  flex: 1 1 0 !important;
  min-height: 0 !important;
}

.meet-pip {
  width: 25.5% !important;
  height: 98% !important;
  border: 1px solid #ccc !important;
  border-radius: 0.25rem !important;
  background: #ffffff !important;
  flex-shrink: 0 !important;
}
</style>
